import React from 'react';
import styled from 'styled-components';
import { PageMeta } from 'components/Layout/Page'; // Assuming this path
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGamepad, faSkullCrossbones, faTrophy, faTicketAlt } from '@fortawesome/free-solid-svg-icons';

const PageWrapper = styled.div`
  color: #e0e0e0;
  font-family: 'Orbitron', sans-serif;
  background-color: #0a0a0f;
  background-image: radial-gradient(ellipse at top, rgba(0, 255, 157, 0.05) 0%, transparent 50%);
`;

const Section = styled.section`
  padding: 100px 40px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 900;
  margin-bottom: 20px;
  color: #00ff9d;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0b0;
  max-width: 800px;
  margin: 0 auto 60px auto;
  line-height: 1.7;
`;

const HeroSection = styled.header`
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  background: url('/images/glitch/glitch-contest-bg.jpg') no-repeat center center/cover; // Recommended: Create a new BG image for the event
  position: relative;

  &::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(ellipse at center, rgba(10, 10, 15, 0.7) 0%, rgba(10, 10, 15, 0.95) 100%);
  }
`;

const HeroContent = styled.div`
  position: relative; z-index: 1; max-width: 900px;
`;

const MainTitle = styled.h1`
  font-size: clamp(3rem, 8vw, 5.5rem); font-weight: 900; color: #fff;
  text-shadow: 0 0 20px rgba(0, 255, 157, 0.5); margin-bottom: 1.5rem; line-height: 1.1;
  text-transform: uppercase;
`;

const CtaButton = styled.a`
  display: inline-block; padding: 15px 40px; font-size: 1.2rem; font-weight: 700;
  color: #0a0a0f; background: #00ff9d; border: none; border-radius: 5px; text-decoration: none;
  cursor: pointer; transition: all 0.3s ease;
  box-shadow: 0 0 25px rgba(0, 255, 157, 0.5); margin: 10px;
  &:hover { transform: scale(1.05); box-shadow: 0 0 40px rgba(0, 255, 157, 0.8); }
`;

const SecondaryCtaButton = styled(CtaButton)`
  background: transparent;
  color: #00ff9d;
  border: 2px solid #00ff9d;
  box-shadow: none;
  &:hover { background: rgba(0, 255, 157, 0.1); box-shadow: 0 0 25px rgba(0, 255, 157, 0.4); }
`;

// --- New components specific to this event page ---
const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
`;

const StepCard = styled.div`
  background: #12121a; padding: 30px; border-radius: 10px;
  border: 1px solid #2a2a3a;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 30px rgba(0, 255, 157, 0.1);
  }
`;

const StepIcon = styled(FontAwesomeIcon)`
  font-size: 3rem; color: #00ff9d; margin-bottom: 20px;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem; margin-bottom: 15px; color: #fff;
`;

const StepDescription = styled.p`
  color: #a0a0b0; line-height: 1.6; font-size: 1rem;
`;

const PrizeTiersContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PrizeTier = styled.div`
  background: #12121a;
  border: 1px solid #2a2a3a;
  border-left: 5px solid #00ff9d;
  padding: 25px;
  border-radius: 8px;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
`;

const TierInfo = styled.div`
  h4 {
    color: #fff;
    font-size: 1.5rem;
    margin: 0 0 5px 0;
  }
  p {
    margin: 0;
    color: #a0a0b0;
    font-weight: 700;
  }
`;

const TierValue = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: #00ff9d;
  background-color: rgba(0, 255, 157, 0.1);
  padding: 10px 20px;
  border-radius: 5px;
`;

const FaqContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
`;

const FaqItem = styled.details`
  background: #12121a;
  border-radius: 5px;
  margin-bottom: 10px;
  border: 1px solid #2a2a3a;
  
  summary {
    font-size: 1.2rem;
    font-weight: 700;
    padding: 20px;
    cursor: pointer;
    list-style: none; /* Hide default arrow */
    position: relative;

    &::-webkit-details-marker {
      display: none;
    }
    
    &:after { /* Custom arrow */
      content: '+';
      color: #00ff9d;
      position: absolute;
      right: 20px;
      font-size: 2rem;
      transition: transform 0.2s ease;
    }
  }

  p {
    padding: 0 20px 20px 20px;
    color: #a0a0b0;
    line-height: 1.7;
    border-top: 1px solid #2a2a3a;
    margin-top: 10px;
    padding-top: 20px;
  }

  &[open] summary:after {
    transform: rotate(45deg);
  }
`;


// --- The Main Event Page Component ---
const GlitchContestPage = () => {
  return (
    <>
      <PageMeta />
      <PageWrapper>
        <main>
          {/* SEO H1: The most important heading for Google */}
          <HeroSection>
            <HeroContent>
              <MainTitle as="h1">The $25,000+ GLITCH Hunter Contest</MainTitle>
              <SectionSubtitle style={{fontSize: '1.3rem', color: '#fff'}}>
                This September, your skill is rewarded. Kill in-game GLITCH bosses to climb the leaderboard and win a share of a massive, community-unlocked prize pool of NFTs and $XP tokens.
              </SectionSubtitle>
              <CtaButton href="/glitch">MINT YOUR GLITCH</CtaButton>
              <SecondaryCtaButton href="/game">JOIN THE HUNT</SecondaryCtaButton>
            </HeroContent>
          </HeroSection>

          {/* HOW TO ENTER SECTION */}
          <Section id="how-to-enter">
            <SectionTitle as="h2">Rules of Engagement</SectionTitle>
            <SectionSubtitle>
              Your mission is simple. The more bosses you defeat, the more entries you earn for the final prize draw. Anyone can join the hunt, but only GLITCH holders have the ultimate advantage. This is a contest of skill, dedication, and strategy.
            </SectionSubtitle>
            <StepsGrid>
              <StepCard>
                <StepIcon icon={faGamepad} />
                <StepTitle as="h3">Join The Hunt</StepTitle>
                {/* This is now the first step, welcoming everyone */}
                <StepDescription>
                  The NEX is free-to-play. Drop into the zone, master the combat, and start hunting GLITCH bosses. Every player can compete for the prize pool.
                </StepDescription>
              </StepCard>
              <StepCard>
                <StepIcon icon={faSkullCrossbones} />
                <StepTitle as="h3">Rack Up Kills</StepTitle>
                {/* This step now clarifies the two-tier entry system */}
                <StepDescription>
                  Every GLITCH boss you eliminate earns you entries. Standard players get 1 entry per kill. GLITCH holders get more. This is where the advantage begins.
                </StepDescription>
              </StepCard>
              <StepCard>
                <StepIcon icon={faTicketAlt} />
                <StepTitle as="h3">Amplify Your Entries</StepTitle>
                {/* This is the core sales pitch for the NFT */}
                <StepDescription>
                  Want to win? Mint a GLITCH NFT. Every kill you make as a holder counts for DOUBLE (2x) the entries, massively increasing your odds in the final giveaway.
                </StepDescription>
              </StepCard>
              <StepCard>
                <StepIcon icon={faTrophy} />
                <StepTitle as="h3">Claim The Spoils</StepTitle>
                {/* This step remains a strong, benefit-focused closing */}
                <StepDescription>
                  Witness the live prize draw at the end of the month. Every kill contributes to unlocking bigger rewards for the entire community. Don't leave your odds to chance.
                </StepDescription>
              </StepCard>
            </StepsGrid>
          </Section>

          {/* PRIZE POOL SECTION */}
          <Section id="prize-pool" style={{backgroundColor: '#050508'}}>
            <SectionTitle as="h2">The Community-Fueled Prize Pool</SectionTitle>
            <SectionSubtitle>The more GLITCHes we kill as a community, the more prizes get unlocked. Let's crush these tiers together!</SectionSubtitle>
            <PrizeTiersContainer>
              <PrizeTier>
                <TierInfo>
                  <h4>Tier 1: The Spark</h4>
                  <p>Community Goal: 100 Kills</p>
                </TierInfo>
                <TierValue>Unlocks 5 GLITCH NFTs (Value: $2,000)</TierValue>
              </PrizeTier>
              <PrizeTier>
                <TierInfo>
                  <h4>Tier 2: The Surge</h4>
                  <p>Community Goal: 250 Kills</p>
                </TierInfo>
                <TierValue>Unlocks +5 MORE NFTs (Total Value: $4,000)</TierValue>
              </PrizeTier>
              <PrizeTier>
                <TierInfo>
                  <h4>Tier 3: The Cascade</h4>
                  <p>Community Goal: 500 Kills</p>
                </TierInfo>
                <TierValue>Unlocks Grand Prize: 4,000 $XP (Total Value: $6,000)</TierValue>
              </PrizeTier>
              <PrizeTier>
                <TierInfo>
                  <h4>Tier 4: The Overload</h4>
                  <p>Community Goal: 1,000 Kills</p>
                </TierInfo>
                <TierValue>Unlocks +10 MORE NFTs & Prizes (Total Value: $25,000+)</TierValue>
              </PrizeTier>
            </PrizeTiersContainer>
            <SecondaryCtaButton href="https://twitter.com/XPthenex" target="_blank" rel="noopener noreferrer" style={{marginTop: '60px'}}>
              Follow for Live Progress Updates
            </SecondaryCtaButton>
          </Section>
          
          {/* FAQ SECTION */}
          <Section id="faq">
            <SectionTitle as="h2">Contest FAQ</SectionTitle>
            <FaqContainer>
              <FaqItem>
                <summary>Do I need a GLITCH NFT to participate?</summary>
                <p>No, you do not! Anyone can play The NEX for free and earn 1 entry per boss kill. However, GLITCH NFT holders gain a massive advantage, earning 2 entries per kill, doubling their chances to win.</p>
              </FaqItem>
              <FaqItem>
                <summary>How do I track my kills and community progress?</summary>
                <p>We will feature a live leaderboard in-game and provide daily updates on our official Twitter and Discord. Every kill you make is automatically logged.</p>
              </FaqItem>
              <FaqItem>
                <summary>When is the prize giveaway?</summary>
                <p>The prize draw will take place during a live stream at the end of September. The exact date and time will be announced on our social channels. Follow us so you don't miss it!</p>
              </FaqItem>
              <FaqItem>
                <summary>Is this a recurring event?</summary>
                <p>Yes! The GLITCH Hunter Contest is a monthly event designed to reward our most dedicated players. Every month brings a new hunt and a new prize pool.</p>
              </FaqItem>
            </FaqContainer>
          </Section>
        </main>
      </PageWrapper>
    </>
  );
};

export default GlitchContestPage;